import { LoadingOutlined } from "@ant-design/icons";
import { Spin, DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import React from "react";
import Card from "../Card";
import Input from "../Input";
import AdminAutocompleteCheckbox from "../AdminAutocompleteCheckbox";

const disabledTimeIfToday = (selectedDate) => {
  if (!selectedDate || !selectedDate.isSame(dayjs(), "day")) return {};
  const now = dayjs();
  return {
    disabledHours: () =>
      Array.from({ length: 24 }, (_, i) => i).filter((h) => h < now.hour()),
    disabledMinutes: (selectedHour) =>
      selectedHour === now.hour()
        ? Array.from({ length: 60 }, (_, i) => i).filter((m) => m <= now.minute())
        : [],
  };
};

const CompanyNotificationForm = ({ formik, dropdownRef }) => {
  const handleIsEveryOne = () => {
    formik.setFieldValue("toEveryOne", !formik.values.toEveryOne);
    const allOfficeIDs = formik.values.companies.map((company) => company._id);
    formik.setFieldValue("selectedItems.office", allOfficeIDs);
  };
  return (
    <div id="company-form" className="py-6">
      <Card>
        <div className="border-b border-stroke py-4 px-6">
          <form onSubmit={formik.handleSubmit}>
            <div className="flex items-end">
              <div className="flex flex-col gap-6 mb-3 w-full md:flex-row">
                {formik.values.selectedItems.agent.length === 0 && (
                  <button
                    onClick={handleIsEveryOne}
                    type="button"
                    className={`flex w-[200px] justify-center rounded ${
                      formik.values.toEveryOne ? "bg-primary" : "bg-slate-300"
                    } p-3 font-medium text-white
                        hover:opacity-90 h-[50px] `}
                  >
                    To All Offices
                  </button>
                )}
                {!formik.values.toEveryOne && (
                  <>
                    {formik.values.selectedItems.agent.length <= 0 && (
                      <AdminAutocompleteCheckbox
                        options={formik.values.companies}
                        formik={formik}
                        rootKey="office"
                        dropdownRef={dropdownRef}
                      />
                    )}
                    {true && (
                      <AdminAutocompleteCheckbox
                        options={formik.values.users}
                        formik={formik}
                        rootKey="agent"
                        dropdownRef={dropdownRef}
                        dependentKey="defaultOffice"
                        dependencyValue={formik.values.selectedItems.office}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="mb-2 flex flex-col gap-3 md:flex-col">
              <div className="w-full ">
                <Input
                  label="Title"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.onBlur}
                  type="text"
                  // touched={formik.touched}
                  error={formik.errors.title}
                  placeholder={"Enter title"}
                />
              </div>

              <div className="w-full ">
                <label className="mb-2 block text-black ">Description</label>
                <textarea
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  type="text"
                  rows={4}
                  placeholder="Enter description"
                  className="w-full rounded-md resize-none border-[1px] border-[#d9d9d9] bg-transparent py-3 px-3 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter placeholder:text-sm "
                />
                {formik.errors.description && (
                  <div className="py-1 text-xs text-red">
                    {formik.errors.description}
                  </div>
                )}
              </div>

              <div className="w-full">
                <label className="mb-2 block text-black">When to send</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => formik.setFieldValue("sendMode", "now")}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded border py-2 text-sm font-semibold ${
                      formik.values.sendMode === "now"
                        ? "border-blue bg-blue/10 text-blue"
                        : "border-[#e3e5e9] text-secondary"
                    }`}
                  >
                    {formik.values.sendMode === "now" && (
                      <span className="inline-block w-2 h-2 rounded-full bg-blue" />
                    )}
                    Send now
                  </button>
                  <button
                    type="button"
                    onClick={() => formik.setFieldValue("sendMode", "schedule")}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded border py-2 text-sm font-semibold ${
                      formik.values.sendMode === "schedule"
                        ? "border-blue bg-blue/10 text-blue"
                        : "border-[#e3e5e9] text-secondary"
                    }`}
                  >
                    {formik.values.sendMode === "schedule" && (
                      <span className="inline-block w-2 h-2 rounded-full bg-blue" />
                    )}
                    Schedule for later
                  </button>
                </div>
                {formik.values.sendMode === "schedule" && (
                  <div className="mb-1">
                    <div className="flex gap-3">
                      <DatePicker
                        allowClear={false}
                        size="large"
                        className="flex-1"
                        placeholder="Select date"
                        value={formik.values.scheduledDate}
                        disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                        onChange={(d) => formik.setFieldValue("scheduledDate", d)}
                        format="YYYY-MM-DD"
                      />
                      <TimePicker
                        use12Hours
                        size="large"
                        className="flex-1"
                        placeholder="Select time"
                        allowClear={false}
                        format="h:mm A"
                        value={formik.values.scheduledTime}
                        onChange={(t) => formik.setFieldValue("scheduledTime", t)}
                        {...disabledTimeIfToday(formik.values.scheduledDate)}
                      />
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      Uses your local time zone
                    </div>
                    {formik.errors.scheduledAt && (
                      <div className="py-1 text-xs text-red">
                        {formik.errors.scheduledAt}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-1 flex justify-end space-x-2">
                <button
                  type="submit"
                  disabled={formik.values.loading}
                  className="flex min-w-[160px] justify-center items-center rounded bg-primary px-5 font-medium text-white hover:opacity-90 h-[42px] whitespace-nowrap"
                >
                  {formik.values.loading ? (
                    <Spin
                      style={{
                        color: "white",
                      }}
                      indicator={
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                      }
                    />
                  ) : formik.values.sendMode === "schedule" ? (
                    "Schedule Notification"
                  ) : (
                    "Send"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default CompanyNotificationForm;
